import React, { useEffect, useState, useCallback } from 'react';
import './styles.css';
import logoImage from './images/logo-center-top.webp';

const API_URL = 'https://p70v8eth93.execute-api.us-east-1.amazonaws.com/test/test-results';

const columns = [
  'Fecha',
  'Nombre',
  'Cedula',
  'Correo Electronico',
  'Escolaridad',
  'Total',
  'Centiles',
  'Punt. S',
];

function removeAccents(str) {
  return str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

// Helper function to format cedula (remove decimal places)
function formatCedula(cedula) {
  if (cedula === null || cedula === undefined) return '';
  // Convert to string and remove decimal part if it's a number
  const cedulaStr = String(cedula);
  return cedulaStr.includes('.') ? cedulaStr.split('.')[0] : cedulaStr;
}

// Helper function to capitalize first letter of each word
function capitalizeName(name) {
  if (!name) return '';
  
  // Convert to lowercase first
  const lowerName = name.toLowerCase();
  
  // Split by spaces and handle each word
  return lowerName.split(' ').map(word => {
    if (!word) return word;
    
    // Handle special cases where certain letters shouldn't be capitalized in the middle
    // Common prefixes like Mc, Mac, O', etc.
    if (word.startsWith('mc') && word.length > 2) {
      return 'Mc' + word.charAt(2).toUpperCase() + word.slice(3);
    }
    if (word.startsWith('mac') && word.length > 3) {
      return 'Mac' + word.charAt(3).toUpperCase() + word.slice(4);
    }
    if (word.startsWith("o'") && word.length > 2) {
      return "O'" + word.charAt(2).toUpperCase() + word.slice(3);
    }
    if (word.startsWith("d'") && word.length > 2) {
      return "D'" + word.charAt(2).toUpperCase() + word.slice(3);
    }
    if (word.startsWith("l'") && word.length > 2) {
      return "L'" + word.charAt(2).toUpperCase() + word.slice(3);
    }
    
    // For regular words, just capitalize the first letter
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
}

const ApplicantTable = () => {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({ name: '', date: '', cedula: '' });
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // Fetch data
  useEffect(() => {
    setIsLoading(true);
    fetch(API_URL)
      .then(response => response.json())
      .then(result => {
        const arrayData = Array.isArray(result.body) ? result.body : JSON.parse(result.body);
        setData(arrayData);
        setFilteredData(arrayData);
        setIsLoading(false);
      })
      .catch(error => {
        console.error(error);
        setIsLoading(false);
      });
  }, []);

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = filteredData.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, data]);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Sorting function
  const sortData = useCallback((dataToSort, key, direction) => {
    return [...dataToSort].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      // Handle special cases for numeric columns
      if (key === 'Cedula' || key === 'Centiles' || key === 'Punt. S') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (key === 'Total') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        // For text columns, convert to lowercase for proper sorting
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }

      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, []);

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filtering
  const applyFilters = useCallback(() => {
    let filtered = data.filter(item => {
      const matchesName = removeAccents((item.Nombre || '').toLowerCase()).includes(removeAccents(filters.name.toLowerCase()));
      const matchesDate = (item.Fecha || '').includes(filters.date);
      const matchesCedula = (item.Cedula || '').includes(filters.cedula);
      return matchesName && matchesDate && matchesCedula;
    });

    // Apply sorting if configured
    if (sortConfig.key) {
      filtered = sortData(filtered, sortConfig.key, sortConfig.direction);
    }

    setFilteredData(filtered);
  }, [data, filters, sortConfig, sortData]);

  useEffect(() => {
    applyFilters();
  }, [filters, data, sortConfig, applyFilters]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: name === 'cedula' ? value.replace(/\D/g, '') : value }));
  };

  // Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <>
      <header className="main-header">
        <div className="header-content">
          <div className="header-logo">
            <img src={logoImage} alt="Logo" />
          </div>
        </div>
      </header>
      <div className="container">
        <h1>Applicant Data</h1>
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading applicant data...</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                <div>
                  <label htmlFor="nameFilter" style={{ display: 'block', marginBottom: 4, fontWeight: '500', color: '#374151' }}>
                    Filtrar por nombre
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="nameFilter"
                    placeholder="Buscar por nombre"
                    value={filters.name}
                    onChange={handleInputChange}
                    style={{ width: 140 }}
                  />
                </div>
                <div>
                  <label htmlFor="dateFilter" style={{ display: 'block', marginBottom: 4, fontWeight: '500', color: '#374151' }}>
                    Filtrar por fecha
                  </label>
                  <input
                    type="date"
                    name="date"
                    id="dateFilter"
                    placeholder="Buscar por fecha"
                    value={filters.date}
                    onChange={handleInputChange}
                    style={{ width: 140 }}
                  />
                </div>
                <div>
                  <label htmlFor="cedulaFilter" style={{ display: 'block', marginBottom: 4, fontWeight: '500', color: '#374151' }}>
                    Filtrar por cédula
                  </label>
                  <input
                    type="text"
                    name="cedula"
                    id="cedulaFilter"
                    placeholder="Buscar por cédula"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    value={filters.cedula}
                    onChange={handleInputChange}
                    style={{ width: 140 }}
                  />
                </div>
              </div>
            </div>
            <table id="applicantTable" className="display">
              <thead>
                <tr>
                  {columns.map(col => (
                    <th 
                      key={col} 
                      onClick={() => handleSort(col)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title={`Click to sort by ${col}`}
                    >
                      {col} {getSortIndicator(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentRecords.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.Fecha}</td>
                    <td>{capitalizeName(item.Nombre)}</td>
                    <td>{formatCedula(item.Cedula)}</td>
                    <td>{item["Correo Electronico"]}</td>
                    <td>{item.Escolaridad}</td>
                    <td>{item.Total}</td>
                    <td>{formatCedula(item.Centiles)}</td>
                    <td>{formatCedula(item["Punt. S"])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {filteredData.length > recordsPerPage && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                marginTop: 20,
                gap: 10
              }}>
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: currentPage === 1 ? '#f3f4f6' : '#fff',
                    color: currentPage === 1 ? '#9ca3af' : '#374151',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Anterior
                </button>
                
                <span style={{ 
                  padding: '8px 12px', 
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  Página {currentPage} de {totalPages}
                </span>
                
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: currentPage === totalPages ? '#f3f4f6' : '#fff',
                    color: currentPage === totalPages ? '#9ca3af' : '#374151',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Siguiente
                </button>
              </div>
            )}
            
            {/* Records count */}
            <div style={{ 
              textAlign: 'center', 
              marginTop: 10,
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Mostrando {startIndex + 1}-{Math.min(endIndex, filteredData.length)} de {filteredData.length} registros
            </div>
          </>
        )}
      </div>
      <footer className="main-footer">
        <div className="footer-content">
          <div className="footer-left">Version 1.0</div>
          <div className="footer-center">© 2025 Blend360 Latam</div>
          <div className="footer-image">
            <img src={logoImage} alt="Logo" />
          </div>
        </div>
      </footer>
    </>
  );
};

export default ApplicantTable; 